// Extracao heuristica dos dados da oferta a partir do TEXTO da mensagem de
// origem (titulo, preco, preco original, desconto, cupom). Alimenta o filtro por
// valor e as variaveis do template.
//
// ponytail: heuristica sobre texto livre. Com 2+ valores em R$, o maior e o
// preco original e o menor e o preco final (padrao "de R$ X por R$ Y" dos canais
// de oferta). Quando houver API de produto por loja, preferir o dado da API.
const MAX_TITLE_LENGTH = 200;
const MONEY_RE = /R\s?\$\s*(\d{1,3}(?:\.\d{3})+|\d+)(?:,(\d{1,2}))?/gi;
const DISCOUNT_RE = /(\d{1,2})\s?%\s?(?:off|de\s+desconto|desc)/i;
const NEGATIVE_DISCOUNT_RE = /-\s?(\d{1,2})\s?%/;
const COUPON_RE = /cupom\s*(?:de\s+desconto)?\s*[:-]?\s*([A-Za-z0-9_-]{3,30})/i;
const URL_RE = /https?:\/\/\S+/i;
const LEADING_NOISE_RE = /^[^\p{L}\p{N}]+/u;
/** "1.299" + "9" -> 129990 centavos. */
function toCents(integerPart, decimals) {
    const reais = Number(integerPart.replace(/\./g, ""));
    const cents = decimals === undefined ? 0 : Number(decimals.padEnd(2, "0"));
    return reais * 100 + cents;
}
/** Todos os valores em R$ do texto, na ordem em que aparecem. */
export function extractMoneyValues(text) {
    return [...text.matchAll(MONEY_RE)].map((m) => toCents(m[1], m[2])).filter((cents) => cents > 0);
}
function extractTitle(text) {
    for (const raw of text.split(/\r?\n/)) {
        const line = raw
            .replace(LEADING_NOISE_RE, "")
            .replace(/[*_~]+/g, "")
            .trim();
        if (line.length < 3 || URL_RE.test(line) || /R\s?\$/i.test(line) || /^cupom/i.test(line)) {
            continue;
        }
        if (!/\p{L}/u.test(line)) {
            continue;
        }
        return line.slice(0, MAX_TITLE_LENGTH);
    }
    return null;
}
/** Desconto explicito em %. Com `skipCouponLines`, ignora % de linha de cupom
 *  (o "10% OFF no cupom" nao e o desconto do produto). */
function extractDiscount(text, skipCouponLines = false) {
    const source = skipCouponLines
        ? text
            .split(/\r?\n/)
            .filter((line) => !/cupom/i.test(line))
            .join("\n")
        : text;
    const match = DISCOUNT_RE.exec(source) ?? NEGATIVE_DISCOUNT_RE.exec(source);
    if (!match) {
        return null;
    }
    const value = Number(match[1]);
    return value > 0 ? value : null;
}
/** Desconto inteiro arredondado a partir de original e final. */
export function computeDiscountPercent(originalCents, priceCents) {
    if (originalCents <= 0 || priceCents >= originalCents) {
        return null;
    }
    return Math.round(((originalCents - priceCents) / originalCents) * 100);
}
/** Linhas cujo valor em R$ NAO e preco do produto (cupom, frete, parcela). */
const NON_PRICE_LINE_RE = /cupom|\boff\b|frete|acima de|parcela|\d+\s?x\s|\bx de\b|cashback|econom|poupe|desconto de r\s?\$/i;
/** Valores em R$ das linhas que descrevem o preco do produto. */
export function extractPriceValues(text) {
    return text
        .split(/\r?\n/)
        .filter((line) => !NON_PRICE_LINE_RE.test(line))
        .flatMap(extractMoneyValues);
}
/** Extrai a oferta do texto de origem. Campos nao encontrados ficam null. */
export function extractOffer(text) {
    const values = extractPriceValues(text);
    const priceCents = values.length > 0 ? Math.min(...values) : null;
    const maxValue = values.length > 0 ? Math.max(...values) : null;
    const originalPriceCents = maxValue !== null && priceCents !== null && maxValue > priceCents ? maxValue : null;
    const computed = originalPriceCents !== null && priceCents !== null
        ? computeDiscountPercent(originalPriceCents, priceCents)
        : null;
    const discountPercent = extractDiscount(text, computed !== null) ?? computed;
    const coupon = COUPON_RE.exec(text)?.[1] ?? null;
    return {
        title: extractTitle(text),
        priceCents,
        originalPriceCents,
        discountPercent,
        coupon,
    };
}
/** 129990 -> "R$ 1.299,90". Deterministico (sem depender do Intl do runtime). */
export function formatBrl(cents) {
    const safe = Math.max(0, Math.round(cents));
    const reais = Math.floor(safe / 100);
    const decimals = String(safe % 100).padStart(2, "0");
    const grouped = String(reais).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `R$ ${grouped},${decimals}`;
}
//# sourceMappingURL=offer.js.map